import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./AppointmentDetailsModal.module.css";
import { IAppointment } from "../../types/appointments.types";
import { getAppointmentColors } from "../../utils/fundingSourceColors";
import CancelAppointmentModal from "../CancelAppointmentModal/CancelAppointmentModal";
import EditAppointmentModal from "../EditAppointmentModal/EditAppointmentModal";
import TreatmentProgramBadge from "../TreatmentProgramBadge/TreatmentProgramBadge";
import AppointmentBadges from "../ActionReportBadge/AppointmentBadges";
import SeriesManager from "../SeriesManager/SeriesManager";
import ConvertToSeriesModal from "../ConvertToSeriesModal/ConvertToSeriesModal";
import Modal from "../../../../components/modals/modal/Modal";
import PrivacyProtectionPopup from "../../../../components/modals/privacyProtectionPopup/privacyProtectionPopup";
import PaymentSection from "../../../../components/paymentSection/PaymentSection";
import { userApi } from "../../../../services/api/user.api";
import { wrapUserForReport } from "../../../../utils/user";
import { useAppDispatch, useAppSelector } from "../../../../services/hooks";
import { useActions } from "../../../../services/hooks/useActions";
import { isChargingCustomersSelector } from "../../../../services/selectors/auth.selectors";
import {
  useLazyGetQuestionnaireAnswersForEditQuery,
  useLazyGetDefaultReportDataQuery,
  useInsertAllAnswersQuestionsMutation,
} from "../../../../services/api/user.api";
import { getDeviceSummaryLabel, getMontageSummary } from "../../../../utils/deviceConfiguration";
import { useGetTreatmentProtocolProgressQuery } from "../../../../services/api/treatmentProtocols.api";
import { useSetAppointmentAttendanceMutation } from "../../../../services/api/utils.api";
import DeviceConfigDetails from "../../../../components/modals/treatmentProtocolDetails/deviceConfigDetails";
import InfoIcon from "../../../../components/icons/InfoIcon";
import sharedModalStyles from "../../../../components/modals/modal/modal.module.css";
import {
  useUpdateQuestionnaireScoreTotalAmountMutation,
} from "../../../../services/api/payment.api";
import { TDefaultReportData } from "../../../../services/types/users.types";
import { TUpdateQuestionnaireScoreTotalAmountRequest } from "../../../../services/types/payment.types";
import { TInsertAllAnswersRequest } from "../../../../services/types/user.types";

// Get locale based on current language
const getLocale = () => (i18n.language === "he" ? "he-IL" : "en-US");

/** Attendance marks, mirrored from AttendanceStatuses on the server. */
const ATTENDED = 1;
const MISSED = 2;

interface IAppointmentDetailsModalProps {
  appointment: IAppointment;
  onClose: () => void;
  onAppointmentCancelled?: () => void;
}

/**
 * Appointment Details Modal Component
 *
 * Displays comprehensive appointment information including:
 * - Treatment program progress
 * - Appointment details (date, time, type)
 * - Patient and therapist information
 */
const AppointmentDetailsModal: React.FC<IAppointmentDetailsModalProps> = ({
  appointment,
  onClose,
  onAppointmentCancelled,
}) => {
  // ========== STATE ==========
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [openPrivacyPopup, setOpenPrivacyPopup] = useState(false);
  const [showDeviceDetails, setShowDeviceDetails] = useState(false);
  const [selectedUserGuid, setSelectedUserGuid] = useState<string | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<'patient' | 'therapist' | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  
  // Payment section state
  const [scoreUsersId, setScoreUsersId] = useState<number | undefined>(undefined);
  const [reportInitialInfo, setReportInitialInfo] = useState<TDefaultReportData | null>(null);
  const [initialPaymentFromEdit, setInitialPaymentFromEdit] = useState<{
    totalAmount?: number | null;
    companyServiceId?: number | null;
    questionnaireScoreStatusId?: number | null;
  } | null>(null);

  // ========== HOOKS ==========
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const showPaymentFeatures = useAppSelector(isChargingCustomersSelector);
  const { clearPaymentSession } = useActions();

  // ========== DATA FETCHING ==========
  const [getQuestionnaireAnswersForEdit] = useLazyGetQuestionnaireAnswersForEditQuery();
  const [getDefaultReportData] = useLazyGetDefaultReportDataQuery();
  const [insertAllAnswers] = useInsertAllAnswersQuestionsMutation();
  const [updateQuestionnaireScoreTotalAmount] = useUpdateQuestionnaireScoreTotalAmountMutation();

  // ========== COMPUTED VALUES ==========
  const isGoogleEvent = appointment.source === "google_calendar";
  const isOther = !appointment.usersId && !appointment.treatmentGroupId && !appointment.mediaId && !isGoogleEvent;
  const isTherapeuticAppointment = !isOther && !isGoogleEvent && !!appointment.usersId;

  // Attendance is answered here and read from here: the treatment counter no longer infers it
  // from an action report, whose own status list can itself mean the patient never came.
  const [setAttendance, { isLoading: isSavingAttendance }] = useSetAppointmentAttendanceMutation();
  const [attendance, setAttendanceState] = useState<number | null>(
    appointment.attendanceStatusId ?? null,
  );
  const [absenceReason, setAbsenceReason] = useState(appointment.absenceReason ?? "");
  const [attendanceError, setAttendanceError] = useState("");

  useEffect(() => {
    setAttendanceState(appointment.attendanceStatusId ?? null);
    setAbsenceReason(appointment.absenceReason ?? "");
  }, [appointment]);

  /** Nothing to report on before the session has begun; the server refuses it too. */
  const appointmentHasStarted = (() => {
    if (!appointment.date) return false;
    const [year, month, day] = appointment.date.split("-").map(Number);
    const [hours, minutes] = (appointment.time || "00:00").split(":").map(Number);
    return new Date(year, month - 1, day, hours, minutes) <= new Date();
  })();

  const saveAttendance = async (status: number | null) => {
    setAttendanceError("");
    setAttendanceState(status);

    const result = await setAttendance({
      appointmentId: appointment.id,
      attendanceStatusId: status,
      absenceReason: status === MISSED ? absenceReason : null,
    });

    if ("error" in result) {
      const message = (result.error as { data?: { message?: string } })?.data?.message;
      setAttendanceError(message || t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"));
      setAttendanceState(appointment.attendanceStatusId ?? null);
      return;
    }

    // The board colours a no-show red and the counters read the mark, so both are stale the
    // moment it is saved. Same refresh the other edits here trigger — the modal stays open.
    onAppointmentCancelled?.();
  };

  // Only for a device appointment: the treatment number is a position inside a device protocol
  // period, so there is nothing to ask about for anything else.
  const { data: protocolProgress } = useGetTreatmentProtocolProgressQuery(
    { userId: appointment.usersId as number, appointmentId: appointment.id },
    { skip: !appointment.usersId || !appointment.device },
  );

  // ========== EFFECTS ==========
  // Redux state cleanup - prevent data leakage between appointments
  useEffect(() => {
    clearPaymentSession();
    return () => {
      clearPaymentSession();
    };
  }, [clearPaymentSession]);

  // Manage z-index of modals portal when privacy popup is open
  useEffect(() => {
    const modalsPortal = document.getElementById('modals');
    if (!modalsPortal) return;

    if (openPrivacyPopup) {
      // Store original z-index
      const originalZIndex = modalsPortal.style.zIndex;
      modalsPortal.style.zIndex = '10001';

      // Cleanup: restore original z-index
      return () => {
        modalsPortal.style.zIndex = originalZIndex;
      };
    }
  }, [openPrivacyPopup]);

  // Pre-fill payment section with existing data or prepare for new entry
  useEffect(() => {
    if (!showPaymentFeatures || !isTherapeuticAppointment) return;

    if (appointment.actionReportScoreUsersId) {
      // Existing record - fetch and pre-fill
      setScoreUsersId(appointment.actionReportScoreUsersId);
      getQuestionnaireAnswersForEdit(appointment.actionReportScoreUsersId)
        .then((res) => {
          if ('data' in res && res.data) {
            setInitialPaymentFromEdit({
              totalAmount: res.data.TotalAmount,
              companyServiceId: res.data.CompanyServiceId,
              questionnaireScoreStatusId: res.data.QuestionnaireCompaniesScoreUsersStatusesId,
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching questionnaire answers:', error);
        });
    } else if (appointment.usersGuid) {
      
      // No existing record - fetch default questionnaire info for creating new record
      getDefaultReportData(appointment.usersGuid)
        .then((res) => {
          if ('data' in res && res.data?.DefaultReportData[0]) {
            const data = res.data.DefaultReportData[0];
            setReportInitialInfo({
              ...data,
              Cycle: data.Cycle ? data.Cycle + 1 : 1,
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching default report data:', error);
        });
    }
  }, [
    showPaymentFeatures,
    isTherapeuticAppointment,
    appointment.actionReportScoreUsersId,
    appointment.usersGuid,
    getQuestionnaireAnswersForEdit,
    getDefaultReportData,
  ]);

  // ========== EVENT HANDLERS ==========
  const handlePaymentSectionSubmit = async (paymentInfo: TUpdateQuestionnaireScoreTotalAmountRequest) => {
    try {
      // Use existing scoreUsersId or create a new one
      let idToUse = scoreUsersId;

      if (!scoreUsersId) {
        // Create minimal stub to get a new questionnaireCompaniesScoreUsersId
        const answers: TInsertAllAnswersRequest[] = [{
          Id: "0",
          QuestionnaireId: reportInitialInfo?.QuestionnaireId?.toString() ?? "0",
          QuestionsId: "0",
          Answer: "",
          AnswerDate: null,
          QuestionsOptionsIds: "",
          Cycle: reportInitialInfo?.Cycle ?? 1,
        }];

        // Submit minimal report to get questionnaireCompaniesScoreUsersId
        // Use appointment's therapist GUID (not admin) so the QCSU record shows the correct therapist in "All action reports" table
        const response = await insertAllAnswers({
          answers,
          questionnaireCompaniesId: reportInitialInfo?.QuestionnaireCompaniesId ?? -1,
          targetUserGuid: appointment.usersGuid || "0",
          appointmentsId: appointment.id,
        }).unwrap();

        const newScoreUsersId = response?.questionnaireCompaniesScoreUsersId;
        setScoreUsersId(newScoreUsersId);
        idToUse = newScoreUsersId;
      }

      // Update the payment info
      await updateQuestionnaireScoreTotalAmount({
        id: idToUse ?? 0,
        body: paymentInfo,
      }).unwrap();

      // Refresh calendar to show updated data (PaymentSection shows its own success message)
      setTimeout(() => {
        onAppointmentCancelled?.();
      }, 1000);
    } catch (error) {
      console.error("Failed to submit payment section:", error);
      throw error; // Re-throw to let PaymentSection handle the error display
    }
  };

  const handleCancelSuccess = () => {
    setShowCancelModal(false);
    onAppointmentCancelled?.();
    onClose();
  };

  const handleEditSuccess = () => {
    setIsEditMode(false);
    onAppointmentCancelled?.(); // Reuse the same callback to refresh data
    onClose();
  };

  const handleEditCancel = () => {
    setIsEditMode(false);
  };



  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(getLocale(), {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleOpenPatientFile = () => {
    if (!appointment.usersGuid) {
      console.error('Patient GUID not available');
      return;
    }
    
    setSelectedUserGuid(appointment.usersGuid);
    setSelectedUserType('patient');
    setOpenPrivacyPopup(true);
  };

  const handleOpenTherapistFile = () => {
    if (!appointment.therapistsGuid) {
      console.error('Therapist GUID not available');
      return;
    }
    
    setSelectedUserGuid(appointment.therapistsGuid);
    setSelectedUserType('therapist');
    setOpenPrivacyPopup(true);
  };

  const handlePrivacyConfirmed = async () => {
    if (!selectedUserGuid || !selectedUserType) return;
    
    setIsLoadingUser(true);
    
    try {
      // Fetch full user data from API
      const result = await dispatch(
        userApi.endpoints.getUserByGuid.initiate(selectedUserGuid)
      ).unwrap();
      
      // Wrap user data in IUserReport format
      const userReport = wrapUserForReport(result, selectedUserGuid, appointment.companyId);
      
      // Close popups
      setOpenPrivacyPopup(false);
      setIsLoadingUser(false);
      onClose();
      
      // Navigate to user edit page with full user data
      const navigationState: any = { 
        user: userReport,
        fromAppointments: true // Mark that we came from appointments
      };
      
      // For therapist, navigate directly to personal details
      if (selectedUserType === 'therapist') {
        navigationState.initialCategory = 'editPersonalData';
      }
      
      navigate('/users/edit', { state: navigationState });
    } catch (error) {
      console.error('Error loading user:', error);
      setIsLoadingUser(false);
      // TODO: Show error message to user
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        {isEditMode ? (
          /* Edit Mode - Render Edit Form */
          <EditAppointmentModal
            appointment={appointment}
            onClose={handleEditCancel}
            onSuccess={handleEditSuccess}
            isEmbedded={true}
          />
        ) : (
          /* View Mode - Show Appointment Details */
          <>
            {/* Modal Header */}
            <div
              className={styles.modalHeader}
              style={
                isGoogleEvent
                  ? { background: "linear-gradient(135deg, #4285F4 0%, #34a853 100%)" }
                  : {}
              }
            >
              <div className={styles.modalTitle} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>{isGoogleEvent ? "📅" : (isOther ? "📝" : "📋")}</span>
                <span>
                  {isGoogleEvent
                    ? t("GOOGLE_CALENDAR_EVENT_TITLE") || "אירוע גוגל"
                    : (isOther ? (t("OTHER_EVENT") || "אירוע כללי") : t("APPOINTMENTS_DETAILS"))}
                </span>
                <AppointmentBadges
  attendanceStatusId={appointment.attendanceStatusId}
  hasActionReport={appointment.hasActionReport}
  hasTherapistAnswered={appointment.hasTherapistAnswered}
  size={18}
  style={{ display: "flex", alignItems: "center", gap: 2 }}
  as="span"
/>
              </div>
              <button className={styles.closeBtn} onClick={onClose}>
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className={styles.modalBody}>

          {/* Cubes + program card show ONLY for a real series program (a program that has
              a frequency). Everything else — a plain single appointment (which still carries
              a legacy seriesId), or a single-appointment protocol program (no frequency) —
              is not a series, so neither is shown. */}
          {(() => {
            const isSeriesProgram =
              !!appointment.appointmentsProgramsId &&
              (appointment.treatmentProgramSequenceId ?? 0) > 0;

            if (isSeriesProgram) {
              return (
                <>
                  {/* Series/Program Manager — session cubes */}
                  <div className={styles.section}>
                    <div className={styles.sectionTitle}>
                      <div className={styles.sectionIcon}>🔁</div>
                      <span>{t("SERIES_MANAGEMENT") || "סדרת פגישות"}</span>
                    </div>
                    <SeriesManager
                      appointmentsProgramsId={appointment.appointmentsProgramsId}
                      currentAppointmentId={appointment.id}
                      onSuccess={() => onAppointmentCancelled?.()}
                    />
                  </div>

                  {/* Treatment Program Badge */}
                  <TreatmentProgramBadge
                    programName={appointment.treatmentProgramName}
                    totalSessions={appointment.treatmentProgramTotalSessions}
                  />
                </>
              );
            }

            // Not a series — offer to convert a therapeutic single appointment into one.
            if (isTherapeuticAppointment) {
              return (
                <div className={styles.section}>
                  <button
                    type="button"
                    onClick={() => setShowConvertModal(true)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 16px",
                      border: "1px dashed #c4b5fd",
                      background: "#f5f3ff",
                      color: "#6b46c1",
                      borderRadius: "10px",
                      fontSize: "14px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    <span>🔁</span>
                    {t("CONVERT_TO_SERIES") || "הפוך לסדרה"}
                  </button>
                </div>
              );
            }

            return null;
          })()}

          {/* Appointment Details Section */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <div className={styles.sectionIcon}>📋</div>
              <span>{t("APPOINTMENTS_DETAILS")}</span>
            </div>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>
                  {t("APPOINTMENTS_DATE_TIME")}
                </div>
                <div className={styles.infoValue}>
                  {formatDate(appointment.date)} | {appointment.time}-
                  {appointment.endTime || ""}
                </div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>
                  {t("APPOINTMENTS_DURATION")}
                </div>
                <div className={styles.infoValue}>
                  {appointment.duration} {t("APPOINTMENTS_MINUTES")}
                </div>
              </div>
              {appointment.treatmentTypeName && (
                <div className={styles.infoItem}>
                  <div className={styles.infoLabel}>
                    {t("APPOINTMENTS_TREATMENT_TYPE")}
                  </div>
                  <div className={styles.infoValue}>
                    {appointment.treatmentTypeName}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Patient / Event Details Section - Hide for 'Other' */}
          {!isOther && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionIcon}>
                  {isGoogleEvent ? "📄" : "👥"}
                </div>
                <span>
                  {isGoogleEvent
                    ? t("APPOINTMENT_CARD_TITLE") || "פרטי האירוע"
                    : t("APPOINTMENTS_PATIENT_THERAPIST")}
                </span>
              </div>
              <div className={styles.infoGrid}>
                {/* Show treatment group if no patient, and handle Other appointments */}
                {!appointment.patientName && appointment.treatmentGroupName ? (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>
                      {t("APPOINTMENTS_TREATMENT_GROUP")}
                    </div>
                    <div
                      className={styles.infoValue}
                      style={{ color: "#10B981", fontWeight: 500 }}
                    >
                      👥 {appointment.treatmentGroupName}
                    </div>
                  </div>
                ) : !appointment.patientName && !appointment.treatmentGroupName && !isGoogleEvent ? (
                   /* Other / Personal Appointment */
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>
                      {t("APPOINTMENTS_DETAILS")}
                    </div>
                    <div className={styles.infoValue}>
                      📝 {appointment.comments || t("OTHER_EVENT")}
                    </div>
                  </div>
                ) : (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>
                      {t("APPOINTMENTS_PATIENT")}
                    </div>
                    <div
                      className={`${styles.infoValue} ${styles.infoLink}`}
                      onClick={handleOpenPatientFile}
                    >
                      👤 {appointment.patientName}
                    </div>
                    {appointment.patient && (
                      <div className={styles.infoSubtext}>
                        {t("APPOINTMENTS_ID")}: {appointment.patient.idNumber} |{" "}
                        {t("APPOINTMENTS_PHONE")}: {appointment.patient.phone}
                      </div>
                    )}
                  </div>
                )}
                {/* Therapist Field */}
                <div className={styles.infoItem}>
                  <div className={styles.infoLabel}>
                    {t("APPOINTMENTS_THERAPIST")}
                  </div>
                  <div
                    className={`${styles.infoValue} ${styles.infoLink}`}
                    onClick={handleOpenTherapistFile}
                  >
                    👨‍⚕️ {appointment.therapistName}
                  </div>
                  {appointment.therapist && (
                    <div className={styles.infoSubtext}>
                      {appointment.therapist.title} | {t("APPOINTMENTS_LICENSE")}:{" "}
                      {appointment.therapist.licenseNumber}
                    </div>
                  )}
                </div>
                {appointment.device && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>{t("APPOINTMENTS_DEVICE")}</div>
                    <div className={styles.infoValue}>
                      🔌 {getDeviceSummaryLabel(appointment.device)}
                      {/* Opens the full montage, so a technician can read the protocol for this
                          appointment straight off the calendar. */}
                      <button
                        type="button"
                        className={styles.deviceInfoBtn}
                        title={t("VIEW_PROTOCOL_DETAILS")}
                        aria-label={t("VIEW_PROTOCOL_DETAILS")}
                        onClick={() => setShowDeviceDetails(true)}
                      >
                        <InfoIcon />
                      </button>
                    </div>
                    {appointment.device.treatmentRoomName && (
                      <div className={styles.infoSubtext}>
                        📍 {appointment.device.treatmentRoomName}
                      </div>
                    )}
                    {getMontageSummary(appointment.device).length > 0 && (
                      <div className={styles.infoSubtext}>
                        {getMontageSummary(appointment.device).join(" | ")}
                      </div>
                    )}
                  </div>
                )}

                {/* Which treatment of the current protocol period this one is — for either kind of
                    protocol. Counted from the day it was assigned, so it restarts with every new
                    one, and the total only appears when the protocol actually sets a limit. */}
                {(protocolProgress?.sessionNumber ?? 0) > 0 && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>{t("TREATMENT_SESSION_NUMBER")}</div>
                    <div className={styles.infoValue}>
                      {protocolProgress!.sessionNumber}
                      {protocolProgress!.sessionsUntilRescan
                        ? ` / ${protocolProgress!.sessionsUntilRescan}`
                        : ""}
                    </div>
                    <div className={styles.infoSubtext}>{protocolProgress!.name}</div>
                  </div>
                )}
              </div>
            </div>
          )}


          {/* Funding & Location Section - Hide for 'Other' */}
          {!isOther && (appointment.fundingSourceName || appointment.mediaName || appointment.companyName || appointment.patientFakeCompanyName || appointment.treatmentRoomName) && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionIcon}>💳</div>
                <span>{t("APPOINTMENTS_FUNDING_LOCATION")}</span>
              </div>
              <div className={styles.infoGrid}>

                {/* Company Name */}
                {appointment.companyName && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>
                      {t("COMPANY")}
                    </div>
                    <div className={styles.infoValue}>
                      🏢 {appointment.companyName}
                    </div>
                  </div>
                )}
                {/* Sub-Company Name (Fake Company) */}
                {appointment.patientFakeCompanyName && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>
                      {t("SUB_COMPANY")}
                    </div>
                    <div className={styles.infoValue}>
                      🏪 {appointment.patientFakeCompanyName}
                    </div>
                  </div>
                )}


                {/* Media/Location */}
                {appointment.mediaName && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>
                      {t("APPOINTMENTS_MEDIA")}
                    </div>
                    <div className={styles.infoValue}>
                      📍 {appointment.mediaName}
                    </div>
                  </div>
                )}

                {/* Treatment Room */}
                {appointment.treatmentRoomName && (
                  <div className={styles.infoItem}>
                    <div className={styles.infoLabel}>
                      {t("TREATMENT_ROOM")}
                    </div>
                    <div className={styles.infoValue}>
                      🚪 {appointment.treatmentRoomName}
                    </div>
                  </div>
                )}

                {/* Funding Source */}
                {appointment.fundingSourceName &&
                  (() => {
                    const fundingColor = getAppointmentColors(appointment);
                    return (
                      <div className={styles.infoItem}>
                        <div className={styles.infoLabel}>
                          {t("APPOINTMENTS_FUNDING")}
                        </div>
                        <div className={styles.infoValue}>
                          <span
                            className={styles.fundingBadge}
                            style={{
                              backgroundColor: fundingColor.badgeBg,
                              color: fundingColor.textColor,
                            }}
                          >
                            {appointment.fundingSourceName}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
              </div>
            </div>
          )}

          {/* Notes Section */}
          {appointment.comments && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionIcon}>📝</div>
                <span>{isOther ? t("MESSAGE_CONTENT") : t("APPOINTMENTS_NOTES")}</span>
              </div>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <div className={styles.infoValue} style={{ whiteSpace: 'pre-wrap' }}>
                    {appointment.comments}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attendance — every company, paying or not. It is a fact about the session, while the
              payment status below is about billing and only exists once a report does. */}
          {isTherapeuticAppointment && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionIcon}>✅</div>
                <span>{t("ATTENDANCE")}</span>
              </div>

              {!appointmentHasStarted ? (
                <div className={styles.infoSubtext}>{t("ATTENDANCE_APPOINTMENT_NOT_STARTED")}</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div className={styles.attendanceGroup}>
                    <button
                      type="button"
                      className={`${styles.attendanceBtn} ${
                        attendance === ATTENDED ? styles.attendanceBtn_attended : ""
                      }`}
                      disabled={isSavingAttendance}
                      onClick={() => saveAttendance(ATTENDED)}
                    >
                      ✓ {t("ATTENDANCE_ATTENDED")}
                    </button>
                    <button
                      type="button"
                      className={`${styles.attendanceBtn} ${
                        attendance === MISSED ? styles.attendanceBtn_missed : ""
                      }`}
                      disabled={isSavingAttendance}
                      onClick={() => saveAttendance(MISSED)}
                    >
                      ✕ {t("ATTENDANCE_MISSED")}
                    </button>

                    {/* A mark can be wrong, and "not answered yet" is a real state the counter
                        treats differently from either answer — so it has to be reachable back. */}
                    {attendance !== null && (
                      <button
                        type="button"
                        className={styles.attendanceClear}
                        disabled={isSavingAttendance}
                        onClick={() => saveAttendance(null)}
                      >
                        {t("ATTENDANCE_CLEAR")}
                      </button>
                    )}
                  </div>

                  {/* The reason belongs to an absence, so it appears with one and is saved with it. */}
                  {attendance === MISSED && (
                    <textarea
                      className={styles.attendanceReason}
                      rows={2}
                      placeholder={t("ATTENDANCE_ABSENCE_REASON")}
                      value={absenceReason}
                      onChange={(e) => setAbsenceReason(e.target.value)}
                      onBlur={() => saveAttendance(MISSED)}
                    />
                  )}

                  {attendanceError && (
                    <div className={styles.attendanceError}>{attendanceError}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Session Details / Payment Section - Show only for therapeutic appointments in paying companies */}
          {showPaymentFeatures && isTherapeuticAppointment && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionIcon}>💳</div>
                <span>{t("ACTION_REPORT_SESSION_DETAILS") || "פרטי פגישה"}</span>
              </div>
              <PaymentSection
                onSubmit={handlePaymentSectionSubmit}
                patientGuid={appointment.usersGuid}
                initialTotalAmount={initialPaymentFromEdit?.totalAmount}
                initialCompanyServiceId={initialPaymentFromEdit?.companyServiceId}
                initialQuestionnaireScoreStatusId={initialPaymentFromEdit?.questionnaireScoreStatusId}
              />
            </div>
          )}

          {/* Actions Section */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <div className={styles.sectionIcon}>⚡</div>
              <span>{t("APPOINTMENTS_ACTIONS")}</span>
            </div>
            <div className={styles.actionsGrid}>
              <button
                className={styles.actionBtn}
                onClick={() => setShowCancelModal(true)}
                title={""}
              >
                <span className={styles.actionIcon}>🚫</span>
                <span>{t("APPOINTMENTS_CANCEL_APPOINTMENT")}</span>
              </button>
              <button
                className={styles.actionBtn}
                onClick={() => setIsEditMode(true)}
                title={""}
              >
                <span className={styles.actionIcon}>✏️</span>
                <span>{t("APPOINTMENTS_EDIT_APPOINTMENT")}</span>
              </button>
            </div>
          </div>
        </div>
          </>
        )}
      </div>

      {/* Cancel Appointment Modal */}
      {showCancelModal && (
        <CancelAppointmentModal
          appointment={appointment}
          onClose={() => setShowCancelModal(false)}
          onSuccess={handleCancelSuccess}
        />
      )}

      {showConvertModal && (
        <ConvertToSeriesModal
          appointmentId={appointment.id}
          appointmentsProgramsId={appointment.appointmentsProgramsId}
          durationMinutes={appointment.duration}
          onClose={() => setShowConvertModal(false)}
          onSuccess={() => {
            setShowConvertModal(false);
            onAppointmentCancelled?.();
          }}
        />
      )}


      {/* Privacy Protection Popup */}
      {openPrivacyPopup && selectedUserGuid && (
        <Modal
          onClose={() => {
            setOpenPrivacyPopup(false);
            setSelectedUserGuid(null);
            setSelectedUserType(null);
          }}
        >
          {isLoadingUser ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p>{t('LOADING')}...</p>
            </div>
          ) : (
            <PrivacyProtectionPopup
              onClose={handlePrivacyConfirmed}
              clientUsersGuid={selectedUserGuid}
            />
          )}
        </Modal>
      )}

      {/*
        Same panel as on the patient details page — it reuses modal.module.css — but rendered inline
        instead of through the shared Modal, which portals into #modals and would be trapped below
        this modal's overlay. Vertical scrolling comes from .popupWindow__content, exactly as it does
        on the patient page.
      */}
      {showDeviceDetails && appointment.device && (
        <div
          className={styles.deviceDetailsHost}
          onClick={(e) => e.target === e.currentTarget && setShowDeviceDetails(false)}
        >
          <div
            dir={i18n.language === "he" ? "rtl" : "ltr"}
            className={`${sharedModalStyles.popupWindow} ${sharedModalStyles.popupWindow_type_wide} ${styles.deviceDetailsPanel}`}
          >
            <button
              type="button"
              className={sharedModalStyles.popupWindow__closeBtn}
              onClick={() => setShowDeviceDetails(false)}
            />
            <div className={sharedModalStyles.popupWindow__content}>
              <DeviceConfigDetails
                device={appointment.device}
                onClose={() => setShowDeviceDetails(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentDetailsModal;
